import { Directive, inject, Input, OnDestroy, input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

// Services
import { MediaUploadService } from '../../../../services/media-upload.service';

// Models
import { AlertType } from '../../../../../../../shared/_model/alert.model';
import { ButtonShape, ButtonSize, ButtonVariant, ButtonWidth } from '../../../../../../../shared/_model/button.model';
import { CurrentUploadOperation } from '../../../../models/media-upload.model';
import { IntegrationProfile } from '../../../../../../models/integration-profile.model';

// Directives
import { StateHandlerDirective } from '../../../../../../../core/_components/state-handler/state-handler.component';

@Directive()
export abstract class FlowMediaUploadStep extends StateHandlerDirective implements OnDestroy {

  readonly completeFlow$ = input.required<Subject<void>>();
  readonly destroyFlow$ = input.required<Subject<void>>();
  readonly navigateToNextStep$ = input.required<Subject<string>>();
  readonly navigateToPreviousStep$ = input.required<Subject<string>>();
  readonly resetFlow$ = input.required<Subject<void>>();
  @Input() uploadProgress = 0;
  readonly uploadComplete = input(false);
  readonly processingComplete = input(false);

  // Component handler (!IMPORTANT - Must match step identifier for this flow)
  abstract identifier: string;

  // Aliases
  alertType = AlertType;
  buttonShape = ButtonShape;
  buttonSize = ButtonSize;
  buttonVariant = ButtonVariant;
  buttonWidth = ButtonWidth;

  // Form handlers
  form!: FormGroup;

  // Operation handlers
  flowCompleted = false;
  operation?: CurrentUploadOperation | null;
  operationUpdate?: Date;
  selectedIntegrations: IntegrationProfile[] = [];

  // Services
  protected readonly activeModalService = inject(NgbActiveModal);
  protected readonly mediaUploadService = inject(MediaUploadService);

  // Subscriptions
  protected readonly currentUpload$: Subscription;
  protected readonly selectedIntegrations$: Subscription;

  constructor() {
    super();

    // Listen for the upload operation details to display file summary
    this.currentUpload$ = this.mediaUploadService.currentUpload$.subscribe({
      next: (response: CurrentUploadOperation | null) => {
        this.operation = response;
        this.operationUpdate = new Date();
        this.cdr.markForCheck();
      },
    });

    // Retrieve the list of selected integrations.
    this.selectedIntegrations$ = this.mediaUploadService.selectedIntegrations$.subscribe({
      next: (response: IntegrationProfile[]) => {
        this.selectedIntegrations = response;
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    this.currentUpload$?.unsubscribe();
    this.selectedIntegrations$?.unsubscribe();
  }

  /**
   * The ODDR link the post preview should show. For a collection (group) upload
   * this is the single {origin}/c/{groupId} hub link; otherwise the individual
   * {origin}/m/{assetId} media link — matching what the backend appends.
   */
  get previewOddrLink(): string {
    const origin = window.location.origin;
    if (this.mediaUploadService.isGroupUpload && this.mediaUploadService.uploadGroupId) {
      return `${origin}/c/${this.mediaUploadService.uploadGroupId}`;
    }
    return `${origin}/m/${this.operation?.assetId ?? ''}`;
  }

  /** Media type of the upload, for the preview card. */
  get previewMediaType(): 'image' | 'video' {
    return this.operation?.contentType?.startsWith('video/') ? 'video' : 'image';
  }

  /** Number of images in the post (drives the carousel indicator). */
  get previewImageCount(): number {
    return this.mediaUploadService.isGroupUpload ?
      this.mediaUploadService.groupAssetCount :
      1;
  }

  /** Media thumbnail URL to show in the preview, if available yet. */
  get previewThumbnailUrl(): string | null {
    return this.operation?.thumbnailUrl ?? null;
  }

  /**
   * Given a forms based payload, inserts the provider and removes null values for processing.
   * @protected
   * @param provider the name of the provider.
   * @param payload the payload to clean.
   * @memberof FlowMediaUploadStep
   */
  protected cleanIntegrationPayload(provider: string, payload: any): any {
    // Required: remove any property that has a null value
    const cleanedPayload = Object.entries(payload)
      .reduce((acc, [key, value]) => {
        if (value !== null) acc[key] = value;
        return acc;
      }, {} as Partial<any>);

    // Return cleaned payload
    return {
      provider,
      ...cleanedPayload,
    };
  }

  /**
   * Closes the modal and completes the flow.
   * @protected
   * @memberof FlowMediaUploadStep
   */
  protected completeFlow(): void {
    // Complete flow and close modal
    this.flowCompleted = true;
    this.completeFlow$().next();
    this.activeModalService.close();
  }

  /**
   * Destroys the flow and cancels any open requests.
   * @protected
   * @memberof FlowMediaUploadStep
   */
  protected destroyFlow(): void {
    this.destroyFlow$().next();
  }

  /**
   * Moves to the next step of the flow.
   * @protected
   * @memberof FlowMediaUploadStep
   */
  protected goToNextStep(): void {
    this.navigateToNextStep$().next(this.identifier);
  }

  /**
   * Moves to the previous step of the flow.
   * @protected
   * @memberof FlowMediaUploadStep
   */
  protected goToPreviousStep(): void {
    this.navigateToPreviousStep$().next(this.identifier);
  }

  /**
   * Aborts the current upload and resets the flow.
   * @protected
   * @memberof FlowMediaUploadStep
   */
  protected resetFlow(): void {
    this.mediaUploadService.abortUpload().subscribe({
      next: () => this.resetFlow$().next(),
    });
  }
}
